import { Link } from 'react-router-dom';
import type { SidebarBadge, SidebarGroup, SidebarItem } from '../services/types';
import { Badge } from '../ui/badge';

interface SidebarProps {
  /** Flat list of items, or grouped sections. */
  items?: SidebarItem[];
  groups?: SidebarGroup[];
  /** Path of the currently active item (usually `location.pathname`). */
  activePath?: string;
  /** Called when a link is clicked — used to close mobile drawer. */
  onNavigate?: () => void;
  /** Icon-only compact mode (desktop collapse). */
  collapsed?: boolean;
}

export function Sidebar({ items, groups, activePath, onNavigate, collapsed = false }: SidebarProps) {
  const finalGroups: SidebarGroup[] = groups ?? [{ items: items ?? [] }];

  return (
    <nav aria-label="Subsystem navigation" className={'flex flex-col ' + (collapsed ? 'gap-2 py-4 px-2' : 'gap-6 py-4 px-3')}>
      {finalGroups.map((group, i) => (
        <div key={group.heading ?? i}>
          {!collapsed && group.heading && (
            <div className="px-2 mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {group.heading}
            </div>
          )}
          <ul className="flex flex-col gap-0.5">
            {group.items.map((item) => (
              <SidebarLink
                key={item.to}
                item={item}
                active={activePath === item.to || activePath?.startsWith(item.to + '/')}
                onNavigate={onNavigate}
                collapsed={collapsed}
              />
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}

function SidebarLink({
  item,
  active,
  onNavigate,
  collapsed,
}: {
  item: SidebarItem;
  active?: boolean;
  onNavigate?: () => void;
  collapsed?: boolean;
}) {
  const base =
    'flex items-center rounded-md text-sm font-medium transition-colors ' +
    (active
      ? 'bg-primary/10 text-primary'
      : 'text-foreground hover:bg-muted');

  return (
    <li>
      <Link
        to={item.to}
        onClick={onNavigate}
        aria-current={active ? 'page' : undefined}
        className={
          'group relative ' +
          base +
          ' ' +
          (collapsed ? 'justify-center w-10 h-10 mx-auto' : 'gap-2 px-2.5 py-1.5')
        }
      >
        {item.icon && <span className="w-4 h-4 shrink-0">{item.icon}</span>}
        {!collapsed && <span className="truncate flex-1">{item.label}</span>}
        {!collapsed && item.badge && <SidebarItemBadge badge={item.badge} />}
        {collapsed && item.badge && hasVisibleBadge(item.badge) && (
          // Collapsed mode: show a tiny dot only (count would be unreadable
          // at the icon-only width). Per docs/UI_GUIDELINES.md.
          <span
            aria-hidden
            className={
              'absolute top-1 right-1 size-1.5 rounded-full ' +
              (item.badge.tone === 'critical'
                ? 'bg-destructive'
                : item.badge.tone === 'warning'
                  ? 'bg-amber-500'
                  : 'bg-muted-foreground')
            }
          />
        )}
        {collapsed && (
          <span
            role="tooltip"
            className="pointer-events-none invisible opacity-0 group-hover:visible group-hover:opacity-100 absolute left-full ml-2 top-1/2 -translate-y-1/2 z-50 px-2 py-1 text-xs font-medium rounded-md bg-popover text-popover-foreground border whitespace-nowrap shadow-md transition-opacity duration-100"
          >
            {item.label}
          </span>
        )}
      </Link>
    </li>
  );
}

// ── Badge helpers ──────────────────────────────────────────────────────────
// All visual styling routes through the shared <Badge> component (shadcn
// variants), not hand-rolled spans — keeps the look consistent with
// every other Badge in the app and lets us tune the palette in one place.

function hasVisibleBadge(b: SidebarBadge): boolean {
  if (b.count !== undefined && b.count > 0) return true;
  // Dot mode: only when tone is elevated; tone='info' with no count = nothing.
  return b.tone === 'warning' || b.tone === 'critical';
}

function SidebarItemBadge({ badge }: { badge: SidebarBadge }) {
  if (!hasVisibleBadge(badge)) return null;
  const tone = badge.tone ?? 'info';
  const variant =
    tone === 'critical' ? 'destructive' :
    tone === 'warning' ? 'warning' :
    'secondary';

  // Count > 0 → number chip. > 99 → "99+".
  if (badge.count !== undefined && badge.count > 0) {
    const display = badge.count > 99 ? '99+' : String(badge.count);
    return (
      <Badge variant={variant} className="ml-auto text-[10px] h-5 px-1.5 tabular-nums">
        {display}
      </Badge>
    );
  }
  // Dot mode: empty badge with rounded-full + small size, tone-only.
  return (
    <Badge
      variant={variant}
      className="ml-auto h-2 w-2 p-0 rounded-full border-0"
      aria-label={`${tone} indicator`}
    />
  );
}
